import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  category: string;
  date: string;
  breadcrumbLabel: string;
  children: React.ReactNode;
}

export default function ArticleLayout({ title, subtitle, category, date, breadcrumbLabel, children }: ArticleLayoutProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuy.study';
  
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Strona Główna',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: breadcrumbLabel,
        item: `${siteUrl}${window.location.pathname}`,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumbs */}
        <div className="w-full px-4 md:px-6 lg:px-12 py-4 bg-background-100 border-b border-background-200/50">
          <nav aria-label="Breadcrumb" className="max-w-4xl mx-auto">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors">
                  Strona Główna
                </Link>
              </li>
              <li className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="text-foreground-800 font-medium">
                {breadcrumbLabel}
              </li>
            </ol>
          </nav>
        </div>

        {/* BreadcrumbList Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />

        {/* Article Content */}
        <article className="w-full px-4 md:px-6 lg:px-12 py-12 md:py-20">
          <div className="max-w-4xl mx-auto">
            {/* Article Header */}
            <header className="mb-12">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-3 py-1 text-xs font-semibold bg-accent-100 text-accent-700 rounded-full">
                  {category}
                </span>
                <span className="text-xs text-foreground-400">{date}</span>
              </div>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
                {title}
              </h1>

              <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-3xl">
                {subtitle}
              </p>
            </header>

            {/* CTA to main spreadsheet */}
            <div className="bg-accent-50 border border-accent-200 rounded-xl p-6 mb-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground-900 mb-1">Chcesz od razu przejść do produktów?</p>
                <p className="text-xs text-foreground-600">Setki sprawdzonych linków czeka na Ciebie w arkuszu Kakobuy.</p>
              </div>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 rounded-full transition-all whitespace-nowrap cursor-pointer flex-shrink-0"
              >
                Kakobuy Spreadsheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
              </a>
            </div>

            {/* Children content — the actual article body */}
            {children}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}